import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Add, Check, Close, EditOutlined, Remove } from "@mui/icons-material";
import { tokens } from "../../../theme";
import { finalPriceFormatter, priceFormatter } from "../../../utils/Utility";
import { CommissionProfitDetailInfo } from "../../../redux/slice/commission/commissionSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { saveCommissionProfitCosts } from "../../../redux/slice/commission/commissionSlice";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { USER_ROLES } from "../../../constants/applicationConstants";
import { useModalStyles } from "../../common/useModalStyles";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { getV3CommissionTypeDisplayLabel } from "../../../utils/Utility";

interface CommissionProfitSectionProps {
  invoiceId: number;
  profitDetail: CommissionProfitDetailInfo | null;
  /** False until the invoice record is loaded, so the card does not flash the wrong layout. */
  visible?: boolean;
  /** True for Purchase & Bill. Non-P&B renders the mirrored summary with COGs forced to 0. */
  purchaseAndBill?: boolean;
  /** Non-P&B Profit Summary "Invoice Total". Same value as the Invoice Calculation card. */
  mfgInvoiceAmount?: number;
  /** V3 service fee receivable from the manufacturer. Non-P&B only. */
  v3ServiceFeeTotal?: number;
  /** Agency service fee used on Non-P&B (same fee the P&B card deducts). */
  agencyServiceFeeTotal?: number;
  /** Override service fee used on Non-P&B (same fee the P&B card deducts). */
  overrideServiceFeeTotal?: number;
}

export type CommissionProfitViewInput = {
  purchaseAndBill: boolean;
  cogsAmount: number;
  shippingChargesActual: number;
  /** P&B profit-detail invoice total, or the Invoice Calculation Invoice Total on Non-P&B. */
  invoiceTotal: number;
  v3ServiceFeeTotal: number;
  agencyCommissionAmount: number;
  overrideCommissionAmount: number;
};

export type CommissionProfitView = {
  cogsAmount: number;
  v3TotalCost: number;
  invoiceTotal: number;
  v3ServiceFeeTotal: number;
  agencyCommissionAmount: number;
  overrideCommissionAmount: number;
  profit: number;
  profitPercent: number;
};

/**
 * P&B profit = invoice total − V3 cost − agency − OVR.
 * Non-P&B profit = V3 service fee receivable − agency − OVR.
 * Primary Org Total Cost is still shown on Non-P&B, but it is not subtracted from Profit.
 * The Invoice Calculation Invoice Total is shown on Non-P&B and is the Profit % base; it is not V3 revenue.
 * ISR is omitted until that fee is part of this card.
 */
export const deriveCommissionProfitView = (input: CommissionProfitViewInput): CommissionProfitView => {
  const cogsAmount = input.purchaseAndBill ? input.cogsAmount : 0;
  const v3TotalCost = roundCurrency(cogsAmount + input.shippingChargesActual);
  const v3ServiceFeeTotal = input.purchaseAndBill ? 0 : input.v3ServiceFeeTotal;
  const revenue = input.purchaseAndBill ? input.invoiceTotal : v3ServiceFeeTotal;
  const costDeduction = input.purchaseAndBill ? v3TotalCost : 0;
  const profit = roundCurrency(
    revenue - costDeduction - input.agencyCommissionAmount - input.overrideCommissionAmount
  );
  const profitPercent =
    input.invoiceTotal === 0 ? 0 : roundCurrency((profit / input.invoiceTotal) * 100);
  return {
    cogsAmount,
    v3TotalCost,
    invoiceTotal: input.invoiceTotal,
    v3ServiceFeeTotal,
    agencyCommissionAmount: input.agencyCommissionAmount,
    overrideCommissionAmount: input.overrideCommissionAmount,
    profit,
    profitPercent,
  };
};

type EditableField = "cogs" | "shipping";

const roundCurrency = (value: number) => Math.round(value * 100) / 100;

const formatProfitPercent = (value: number) => {
  if (!Number.isFinite(value)) {
    return "0.00%";
  }
  const rounded = roundCurrency(value);
  const formatted = Math.abs(rounded).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return rounded < 0 ? `-${formatted}%` : `${formatted}%`;
};

const ProfitRow: React.FC<{
  label: string;
  value: string;
  labelColor?: string;
  valueColor?: string;
  tokens: ReturnType<typeof tokens>;
}> = ({ label, value, labelColor, valueColor, tokens }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, py: 0.75 }}>
    <Typography
      variant="h6"
      fontWeight="bold"
      sx={{ color: labelColor || tokens.grey[200] }}
    >
      {label}
    </Typography>
    <Typography
      variant="h6"
      sx={{
        color: valueColor || "#22c55e",
        fontWeight: 700,
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const EditableProfitRow: React.FC<{
  label: string;
  value: number;
  field: EditableField;
  editingField: EditableField | null;
  draftValue: string;
  saving: boolean;
  onStartEdit: (field: EditableField, value: number) => void;
  onDraftChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  tokens: ReturnType<typeof tokens>;
  labelColor?: string;
  valueColor?: string;
}> = ({
  label,
  value,
  field,
  editingField,
  draftValue,
  saving,
  onStartEdit,
  onDraftChange,
  onConfirm,
  onCancel,
  tokens,
  labelColor,
  valueColor,
}) => {
  const isEditing = editingField === field;
  const rowLabelColor = labelColor || tokens.grey[200];
  const rowValueColor = valueColor || "#22C55E";

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, py: 0.75 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ color: rowLabelColor }}>
        {label}
      </Typography>

      {isEditing ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="h6" sx={{ color: rowValueColor, fontWeight: 700 }}>
            $
          </Typography>
          <TextField
            type="number"
            size="small"
            value={draftValue}
            onChange={(e) => onDraftChange(e.target.value)}
            autoFocus
            disabled={saving}
            inputProps={{ min: 0, step: 0.01 }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") onConfirm();
              if (e.key === "Escape") onCancel();
            }}
            sx={{
              width: 130,
              "& .MuiOutlinedInput-root": {
                height: 36,
                fontSize: "1.25rem",
                fontVariantNumeric: "tabular-nums",
              },
            }}
          />
          <Tooltip title="Save">
            <span>
              <IconButton
                size="small"
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  onConfirm();
                }}
                disabled={saving}
              >
                <Check fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancel">
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel();
                }}
                disabled={saving}
              >
                <Close fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="h6"
            sx={{
              color: rowValueColor,
              fontWeight: 700,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {priceFormatter(value)}
          </Typography>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              aria-label={`Edit ${label}`}
              onClick={(e) => {
                e.stopPropagation();
                onStartEdit(field, value);
              }}
              sx={{ color: tokens.grey[300] }}
            >
              <EditOutlined sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

const sectionCardSx = (isDark: boolean) => ({
  borderRadius: 3,
  overflow: "hidden",
  backgroundColor: isDark ? "#03112146" : "#e4e4e454",
  border: `1px solid #6d6d6d46`,
});

const profitRowsSx = {
  width: "100%",
  maxWidth: "100%",
  flex: 1,
  display: "flex",
  flexDirection: "column",
};

const CommissionProfitSection: React.FC<CommissionProfitSectionProps> = ({
  invoiceId,
  profitDetail,
  visible = true,
  purchaseAndBill,
  mfgInvoiceAmount = 0,
  v3ServiceFeeTotal = 0,
  agencyServiceFeeTotal = 0,
  overrideServiceFeeTotal = 0,
}) => {
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const v3EntityLabel = useMemo(
    () => getV3CommissionTypeDisplayLabel(allCodeTablesValuesMap),
    [allCodeTablesValuesMap]
  );
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { tokens: modalTokens } = useModalStyles();
  const dispatch = useAppDispatch();
  const { userInfo } = useAuthSlice();

  const isAdminUser =
    userInfo?.userRole === USER_ROLES.ROLE_ADMIN ||
    userInfo?.userRole === USER_ROLES.ROLE_SUPERADMIN;

  const [cogsAmount, setCogsAmount] = useState(0);
  const [shippingChargesActual, setShippingChargesActual] = useState(0);
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [draftValue, setDraftValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [sectionExpanded, setSectionExpanded] = useState(true);

  useEffect(() => {
    if (profitDetail) {
      setCogsAmount(profitDetail.cogsAmount ?? 0);
      setShippingChargesActual(profitDetail.shippingChargesActual ?? 0);
      setEditingField(null);
      setDraftValue("");
    }
  }, [profitDetail]);

  const isPurchaseAndBill = purchaseAndBill ?? !!profitDetail?.purchaseAndBillEnabled;

  const derived = useMemo(
    () =>
      deriveCommissionProfitView({
        purchaseAndBill: isPurchaseAndBill,
        cogsAmount,
        shippingChargesActual,
        invoiceTotal: isPurchaseAndBill ? (profitDetail?.invoiceTotal ?? 0) : mfgInvoiceAmount,
        v3ServiceFeeTotal,
        agencyCommissionAmount: isPurchaseAndBill
          ? (profitDetail?.agencyCommissionAmount ?? 0)
          : agencyServiceFeeTotal,
        overrideCommissionAmount: isPurchaseAndBill
          ? (profitDetail?.overrideCommissionAmount ?? 0)
          : overrideServiceFeeTotal,
      }),
    [
      isPurchaseAndBill,
      cogsAmount,
      shippingChargesActual,
      profitDetail,
      mfgInvoiceAmount,
      v3ServiceFeeTotal,
      agencyServiceFeeTotal,
      overrideServiceFeeTotal,
    ]
  );

  const handleStartEdit = (field: EditableField, value: number) => {
    setEditingField(field);
    setDraftValue(String(value));
  };

  const handleConfirmEdit = async () => {
    const parsed = parseFloat(draftValue);
    if (Number.isNaN(parsed) || parsed < 0 || !invoiceId) {
      return;
    }

    const rounded = roundCurrency(parsed);
    const nextCogs = !isPurchaseAndBill ? 0 : editingField === "cogs" ? rounded : cogsAmount;
    const nextShipping = editingField === "shipping" ? rounded : shippingChargesActual;

    setSaving(true);
    try {
      const response = await dispatch(
        saveCommissionProfitCosts({
          invoiceId,
          cogsAmount: nextCogs,
          shippingChargesActual: nextShipping,
        })
      );
      if (saveCommissionProfitCosts.fulfilled.match(response) && response.payload?.code === "V3OK") {
        setCogsAmount(nextCogs);
        setShippingChargesActual(nextShipping);
        setEditingField(null);
        setDraftValue("");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setDraftValue("");
  };

  if (!isAdminUser || !visible || (!profitDetail && purchaseAndBill !== false)) {
    return null;
  }

  const showOverrideCommission = derived.overrideCommissionAmount > 0;

  return (
    <Grid item xs={12}>
      <Box sx={sectionCardSx(isDark)}>
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={sectionExpanded}
          aria-label={sectionExpanded ? "Collapse Cost and Profit" : "Expand Cost and Profit"}
          onClick={() => setSectionExpanded((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setSectionExpanded((prev) => !prev);
            }
          }}
          sx={{
            px: { xs: 1.5, sm: 2.5 },
            pt: { xs: 2, sm: 2.5 },
            pb: sectionExpanded ? 0 : { xs: 2, sm: 2.5 },
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 1.5,
            cursor: "pointer",
            position: "relative",
          }}
        >
          <IconButton
            size="small"
            aria-hidden="true"
            tabIndex={-1}
            onClick={(event) => {
              event.stopPropagation();
              setSectionExpanded((prev) => !prev);
            }}
            sx={{ position: "absolute", top: 10, right: 10 }}
          >
            {sectionExpanded ? <Remove /> : <Add />}
          </IconButton>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              pr: 5,
            }}
          >
            <Typography variant="h5" fontWeight="bold" color="#22C55E">
              Cost Breakdown &amp; Profit Summary
            </Typography>
          </Box>

          {!sectionExpanded && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1fr)" },
                  gap: 1.5,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${modalTokens.accent}55`,
                    backgroundColor: isDark ? "rgba(20,184,166,0.06)" : "rgba(20,184,166,0.08)",
                  }}
                >
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: modalTokens.accent, mb: 0.5 }}>
                    {`${v3EntityLabel} Total Cost`}
                  </Typography>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: modalTokens.accent, fontVariantNumeric: "tabular-nums" }}>
                    {priceFormatter(derived.v3TotalCost)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    border: "1px solid #22c55e55",
                    backgroundColor: isDark ? "rgba(34,197,94,0.06)" : "rgba(34,197,94,0.08)",
                  }}
                >
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#22C55E", mb: 0.5 }}>
                    Profit
                  </Typography>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: "#22C55E", fontVariantNumeric: "tabular-nums" }}>
                    {finalPriceFormatter(derived.profit)}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1, mt: 0.75 }}>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#22C55E" }}>
                      Profit %
                    </Typography>
                    <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: "#22C55E", fontVariantNumeric: "tabular-nums" }}>
                      {formatProfitPercent(derived.profitPercent)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
          )}
        </Box>

        <Collapse in={sectionExpanded}>
          <Box
            sx={{
              px: { xs: 1.5, sm: 2.5 },
              pt: 1.5,
              pb: { xs: 2, sm: 2.5 },
            }}
          >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                  gap: 1.5,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.75,
                    borderRadius: 2,
                    border: `1px solid ${modalTokens.accent}55`,
                    backgroundColor: isDark ? "rgba(20,184,166,0.06)" : "rgba(20,184,166,0.08)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: modalTokens.accent, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.85 }}>
                    Cost Breakdown
                  </Typography>
                  <Box sx={profitRowsSx}>
                    {isPurchaseAndBill ? (
                    <EditableProfitRow
                      label="COGs"
                      value={cogsAmount}
                      field="cogs"
                      editingField={editingField}
                      draftValue={draftValue}
                      saving={saving}
                      onStartEdit={handleStartEdit}
                      onDraftChange={setDraftValue}
                      onConfirm={handleConfirmEdit}
                      onCancel={handleCancelEdit}
                      tokens={colors}
                      labelColor={isDark ? "#fff" : modalTokens.textPrimary}
                      valueColor={isDark ? "#fff" : modalTokens.textPrimary}
                    />
                    ) : (
                      <ProfitRow
                        label="COGs"
                        value={priceFormatter(0)}
                        labelColor={isDark ? "#fff" : modalTokens.textPrimary}
                        valueColor={isDark ? "#fff" : modalTokens.textPrimary}
                        tokens={colors}
                      />
                    )}
                    <EditableProfitRow
                      label="Shipping Charges (Actual)"
                      value={shippingChargesActual}
                      field="shipping"
                      editingField={editingField}
                      draftValue={draftValue}
                      saving={saving}
                      onStartEdit={handleStartEdit}
                      onDraftChange={setDraftValue}
                      onConfirm={handleConfirmEdit}
                      onCancel={handleCancelEdit}
                      tokens={colors}
                      labelColor={isDark ? "#fff" : modalTokens.textPrimary}
                      valueColor={isDark ? "#fff" : modalTokens.textPrimary}
                    />
                    <Box sx={{ mt: "auto" }}>
                      <Divider sx={{ my: 1.5, borderColor: `${modalTokens.accent}55` }} />
                      <ProfitRow
                        label={`${v3EntityLabel} Total Cost`}
                        value={priceFormatter(derived.v3TotalCost)}
                        labelColor={modalTokens.accent}
                        valueColor={modalTokens.accent}
                        tokens={colors}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    px: 2,
                    py: 1.75,
                    borderRadius: 2,
                    border: "1px solid #22c55e55",
                    backgroundColor: isDark ? "rgba(34,197,94,0.06)" : "rgba(34,197,94,0.08)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#22C55E", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.85 }}>
                    Profit Summary
                  </Typography>
                  <Box sx={profitRowsSx}>
                    <ProfitRow
                      label="Invoice Total"
                      value={priceFormatter(derived.invoiceTotal)}
                      labelColor={isDark ? "#fff" : modalTokens.textPrimary}
                      valueColor={isDark ? "#fff" : modalTokens.textPrimary}
                      tokens={colors}
                    />
                    {!isPurchaseAndBill && (
                      <ProfitRow
                        label={`${v3EntityLabel} Service Fee Total`}
                        value={priceFormatter(derived.v3ServiceFeeTotal)}
                        labelColor={isDark ? "#fff" : modalTokens.textPrimary}
                        valueColor={isDark ? "#fff" : modalTokens.textPrimary}
                        tokens={colors}
                      />
                    )}
                    <ProfitRow
                      label={`Less: ${v3EntityLabel} Total Cost`}
                      value={`- ${priceFormatter(derived.v3TotalCost)}`}
                      labelColor={isDark ? "#fff" : modalTokens.textPrimary}
                      valueColor={isDark ? "#fff" : modalTokens.textPrimary}
                      tokens={colors}
                    />
                    <ProfitRow
                      label="Less: Agency Service Fee"
                      value={`- ${priceFormatter(derived.agencyCommissionAmount)}`}
                      labelColor={isDark ? "#fff" : modalTokens.textPrimary}
                      valueColor={isDark ? "#fff" : modalTokens.textPrimary}
                      tokens={colors}
                    />
                    {showOverrideCommission && (
                      <ProfitRow
                        label="Less: OVR Service Fee"
                        value={`- ${priceFormatter(derived.overrideCommissionAmount)}`}
                        labelColor={isDark ? "#fff" : modalTokens.textPrimary}
                        valueColor={isDark ? "#fff" : modalTokens.textPrimary}
                        tokens={colors}
                      />
                    )}
                    <Box sx={{ mt: "auto" }}>
                      <Divider sx={{ my: 1.5, borderColor: "rgba(34,197,94,0.35)" }} />
                      <ProfitRow
                        label="Profit"
                        value={finalPriceFormatter(derived.profit)}
                        labelColor="#22C55E"
                        valueColor="#22C55E"
                        tokens={colors}
                      />
                      <ProfitRow
                        label="Profit %"
                        value={formatProfitPercent(derived.profitPercent)}
                        labelColor="#22C55E"
                        valueColor="#22C55E"
                        tokens={colors}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
          </Box>
        </Collapse>
      </Box>
    </Grid>
  );
};

export default CommissionProfitSection;
